import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { MobileNav } from '../components/MobileNav';
// --- Visual System Components ---
// Coordinate Grid - System coordinates, not decoration
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.02] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '60px 60px'
}} aria-hidden="true" data-id="element-1581" />;
// Pattern Emergence Animation - Shows patterns forming over time
const PatternEmergence = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(p => (p + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  return <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-1582">
      {/* Nodes that connect and disconnect - showing feedback loops */}
      <circle cx="300" cy="400" r="4" fill="#64748B" opacity={phase >= 1 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1583" />
      <circle cx="600" cy="300" r="4" fill="#64748B" opacity={phase >= 2 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1584" />
      <circle cx="900" cy="400" r="4" fill="#64748B" opacity={phase >= 3 ? 1 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1585" />

      {/* Connecting lines - emerge sequentially */}
      <path d="M300 400 L600 300" stroke="#64748B" strokeWidth="0.5" opacity={phase >= 2 ? 0.5 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1586" />
      <path d="M600 300 L900 400" stroke="#64748B" strokeWidth="0.5" opacity={phase >= 3 ? 0.5 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1587" />
      <path d="M900 400 L300 400" stroke="#64748B" strokeWidth="0.5" strokeDasharray="4 4" opacity={phase === 0 ? 0.3 : 0} style={{
      transition: 'opacity 1s ease-in-out'
    }} data-id="element-1588" />
    </svg>;
};
// --- Sections ---
// 1. HERO
const HeroSection = () => <section className="relative min-h-screen flex flex-col justify-center px-4 sm:px-6 pt-24 sm:pt-20 pb-12 sm:pb-16 overflow-hidden bg-[#0A0A0A]" data-id="element-1589">
    <CoordinateGrid data-id="element-1590" />
    <PatternEmergence data-id="element-1591" />

    <div className="relative z-10 max-w-5xl mx-auto space-y-8 sm:space-y-12 w-full" data-id="element-1592">
      {/* Headline */}
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif leading-[1.15] text-[#F5F5F5] tracking-tight" style={{
      fontFamily: 'Georgia, serif'
    }} data-id="element-1593">
        We build instruments to decode human patterns.
      </h1>

      {/* Subheadline */}
      <div className="space-y-4 sm:space-y-6 max-w-3xl" data-id="element-1594">
        <p className="text-lg sm:text-xl md:text-2xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-1595">
          Lazy Monk Labs is a pattern recognition company. We build systems that
          reveal the repeating structures behind how people think, decide, and
          act — at work and in life.
        </p>
      </div>

      {/* Primary CTAs */}
      <div className="pt-6 sm:pt-8 flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-1596">
        <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1597">
          <Button className="w-full sm:w-auto bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-6 sm:px-8 py-5 sm:py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1598">
            Explore Decodes
          </Button>
        </a>
        <Link to="/mirrors/work" className="w-full sm:w-auto" data-id="element-1599">
          <Button className="w-full sm:w-auto bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-6 sm:px-8 py-5 sm:py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1600">
            Explore Work Mirror
          </Button>
        </Link>
      </div>

      {/* Small line below */}
      <div className="pt-3 sm:pt-4" data-id="element-1601">
        <p className="text-xs sm:text-sm text-gray-600" data-id="element-1602">
          Powered by Decoder — our pattern recognition engine.
        </p>
      </div>
    </div>
  </section>;
// 2. TWO WAYS TO DECODE YOUR PATTERNS
const TwoProductsSection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1603">
    <div className="max-w-6xl mx-auto" data-id="element-1604">
      <div className="space-y-12 sm:space-y-16" data-id="element-1605">
        {/* Title */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1606">
          Two ways to decode your patterns
        </h2>

        {/* Two equal cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" data-id="element-1607">
          {/* LEFT - DECODES */}
          <div className="border border-[#2B2B2B] p-6 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 bg-[#0A0A0A]" data-id="element-1608">
            <div className="space-y-3 sm:space-y-4" data-id="element-1609">
              <h3 className="text-xl sm:text-2xl font-medium text-[#F5F5F5]" data-id="element-1610">
                Decodes
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-1611">
                One-time structural decodes of specific areas of life and work.
                These are precise, point-in-time interpretations — designed to
                show you the underlying pattern you keep repeating.
              </p>
            </div>

            <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-500" data-id="element-1612">
              <p data-id="element-1613">• Self Decode</p>
              <p data-id="element-1614">• Work Decode</p>
              <p data-id="element-1615">• Relationship Decode</p>
              <p data-id="element-1616">• Child Decode</p>
            </div>

            <div className="pt-3 sm:pt-4 border-t border-[#2B2B2B]" data-id="element-1617">
              <p className="text-xs text-gray-600" data-id="element-1618">
                One-time • Immediate clarity • Shareable
              </p>
            </div>

            <div className="pt-3 sm:pt-4" data-id="element-1619">
              <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="block" data-id="element-1620">
                <Button className="w-full bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium" data-id="element-1621">
                  Explore Decodes
                </Button>
              </a>
            </div>
          </div>

          {/* RIGHT - WORK MIRROR */}
          <div className="border border-[#2B2B2B] p-6 sm:p-8 lg:p-10 space-y-6 sm:space-y-8 bg-[#0A0A0A]" data-id="element-1622">
            <div className="space-y-3 sm:space-y-4" data-id="element-1623">
              <h3 className="text-xl sm:text-2xl font-medium text-[#F5F5F5]" data-id="element-1624">
                Work Mirror
              </h3>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-1625">
                A living mirror that continuously decodes how your work pattern
                shows up in real situations over time. Instead of a one-time
                decode, this lets you watch the pattern in motion.
              </p>
            </div>

            <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-500" data-id="element-1626">
              <p data-id="element-1627">• Log situations, modes, and days</p>
              <p data-id="element-1628">• See recurring loops and structures</p>
              <p data-id="element-1629">• Build a long-range record of how you operate</p>
            </div>

            <div className="pt-3 sm:pt-4 border-t border-[#2B2B2B]" data-id="element-1630">
              <p className="text-xs text-gray-600" data-id="element-1631">
                Ongoing • Long-term • Builds over time
              </p>
            </div>

            <div className="pt-3 sm:pt-4" data-id="element-1632">
              <Link to="/mirrors/work" className="block" data-id="element-1633">
                <Button className="w-full bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium" data-id="element-1634">
                  Explore Work Mirror
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 3. DECODER (THE ENGINE)
const DecoderEngineSection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1635">
    <div className="max-w-4xl mx-auto" data-id="element-1636">
      <div className="space-y-8 sm:space-y-12" data-id="element-1637">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1638">
          Everything is powered by Decoder
        </h2>

        <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1639">
          <p data-id="element-1640">
            Decoder is our pattern recognition engine. It is not psychology. Not
            coaching. Not journaling. It is a system for detecting structural
            patterns in human behavior.
          </p>

          <p data-id="element-1641">Decoder powers both:</p>

          <div className="pl-4 sm:pl-6 border-l-2 border-[#2B2B2B] space-y-3 sm:space-y-4" data-id="element-1642">
            <p data-id="element-1643">• Our Decodes (one-time structural interpretations)</p>
            <p data-id="element-1644">• And the Work Mirror (a continuous living interpretation)</p>
          </div>

          <p className="pt-4 sm:pt-6 border-t border-[#2B2B2B] text-[#F5F5F5]" data-id="element-1645">
            It is the same engine, applied in two time horizons:
            <br data-id="element-1646" />
            <br data-id="element-1647" />
            One-time decodes.
            <br data-id="element-1648" />
            And living systems.
          </p>
        </div>

        <div className="pt-6 sm:pt-8" data-id="element-1649">
          <Link to="/decoder" className="inline-flex items-center gap-3 text-sm sm:text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-1650">
            <span data-id="element-1651">Learn about Decoder</span>
            <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-1652">
              →
            </span>
          </Link>
        </div>
      </div>
    </div>
  </section>;
// 4. WHAT WE ARE NOT
const WhatWeAreNotSection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1653">
    <div className="max-w-6xl mx-auto" data-id="element-1654">
      <div className="space-y-12 sm:space-y-16" data-id="element-1655">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1656">
          This is not self-help.
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12" data-id="element-1657">
          <div className="space-y-3 sm:space-y-4 text-center" data-id="element-1658">
            <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-1659">
              Not Therapy
            </h3>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-1660">
              We don't work with emotions or stories. We work with structure.
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4 text-center" data-id="element-1661">
            <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-1662">
              Not Productivity
            </h3>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-1663">
              We don't optimize output. We observe patterns.
            </p>
          </div>

          <div className="space-y-3 sm:space-y-4 text-center" data-id="element-1664">
            <h3 className="text-lg sm:text-xl font-medium text-[#F5F5F5]" data-id="element-1665">
              Not Advice
            </h3>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-1666">
              We don't tell you what to do. We decode what you keep doing.
            </p>
          </div>
        </div>

        <div className="pt-6 sm:pt-8 text-center" data-id="element-1667">
          <p className="text-base sm:text-lg text-[#F5F5F5] leading-relaxed" data-id="element-1668">
            Our job is not to change you. Our job is to make the pattern
            visible.
          </p>
        </div>
      </div>
    </div>
  </section>;
// 5. HOW PEOPLE USE THIS
const HowPeopleUseSection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1669">
    <div className="max-w-6xl mx-auto" data-id="element-1670">
      <div className="space-y-12 sm:space-y-16" data-id="element-1671">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1672">
          How people use Lazy Monk Labs
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-12" data-id="element-1673">
          <div className="space-y-4 sm:space-y-6" data-id="element-1674">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1675">
              As a Decode
            </p>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1676">
              "I want to see my pattern in my work / relationship / life."
            </p>
            <p className="text-sm sm:text-base text-[#F5F5F5]" data-id="element-1677">→ Use Decodes</p>
          </div>

          <div className="space-y-4 sm:space-y-6" data-id="element-1678">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1679">
              As a Mirror
            </p>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1680">
              "I want to watch how this pattern plays out over time."
            </p>
            <p className="text-sm sm:text-base text-[#F5F5F5]" data-id="element-1681">
              → Use Work Mirror
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6" data-id="element-1682">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1683">
              As a System
            </p>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1684">
              "I want a long-term record of how I actually operate."
            </p>
            <p className="text-sm sm:text-base text-[#F5F5F5]" data-id="element-1685">→ Use both</p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 6. PHILOSOPHY / CREDIBILITY
const PhilosophySection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1686">
    <div className="max-w-4xl mx-auto" data-id="element-1687">
      <div className="space-y-8 sm:space-y-12" data-id="element-1688">
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1689">
          Built for people who care about how they operate
        </h2>

        <div className="space-y-6 sm:space-y-8 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-1690">
          <p data-id="element-1691">
            Lazy Monk Labs is building a new category: pattern recognition
            instruments for human systems.
          </p>

          <div className="pl-4 sm:pl-6 border-l-2 border-[#2B2B2B] space-y-3 sm:space-y-4" data-id="element-1692">
            <p className="text-gray-500" data-id="element-1693">We believe:</p>
            <p data-id="element-1694">• Most problems repeat.</p>
            <p data-id="element-1695">• Most people repeat.</p>
            <p data-id="element-1696">• But nobody can see the loop while they are inside it.</p>
          </div>

          <p className="text-[#F5F5F5] pt-4 sm:pt-6 border-t border-[#2B2B2B]" data-id="element-1697">
            We build decoders and mirrors for that.
          </p>
        </div>
      </div>
    </div>
  </section>;
// 7. FINAL CTA
const FinalCTASection = () => <section className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1698">
    <div className="max-w-4xl mx-auto text-center space-y-8 sm:space-y-12" data-id="element-1699">
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center" data-id="element-1700">
        <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1701">
          <Button className="w-full sm:w-auto bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 sm:px-12 py-5 sm:py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1702">
            Explore Decodes
          </Button>
        </a>
        <Link to="/mirrors/work" className="w-full sm:w-auto" data-id="element-1703">
          <Button className="w-full sm:w-auto bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 sm:px-12 py-5 sm:py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1704">
            Explore Work Mirror
          </Button>
        </Link>
      </div>

      <div data-id="element-1705">
        <Link to="/decoder" className="inline-flex items-center gap-3 text-xs sm:text-sm text-gray-600 hover:text-gray-400 transition-colors group" data-id="element-1706">
          <span data-id="element-1707">Or learn how the engine works</span>
          <span className="group-hover:text-gray-400 transition-colors" data-id="element-1708">→</span>
        </Link>
      </div>
    </div>
  </section>;
// 8. FOOTER
const Footer = () => <footer className="py-8 sm:py-12 px-4 sm:px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1709">
    <div className="max-w-6xl mx-auto" data-id="element-1710">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8" data-id="element-1711">
        <div data-id="element-1712">
          <p className="text-sm text-gray-500 font-mono" data-id="element-1713">Lazy Monk Labs</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1714">
            Pattern recognition systems
          </p>
        </div>

        <div className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8 text-sm text-gray-500" data-id="element-1715">
          <a href="https://decoder-website.vercel.app/reports" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1716">
            Decodes
          </a>
          <Link to="/mirrors/work" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1717">
            Work Mirror
          </Link>
          <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1718">
            Decoder
          </Link>
          <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1719">
            Careers
          </Link>
          <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1720">
            Contact
          </a>
        </div>

        <div className="text-xs text-gray-600 font-mono" data-id="element-1721">
          © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  </footer>;
// --- Main Component ---
export function Home() {
  return <main className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] selection:bg-[#64748B] selection:text-white" data-id="element-1722">
      <MobileNav data-id="element-1723" />
      <HeroSection data-id="element-1724" />
      <TwoProductsSection data-id="element-1725" />
      <DecoderEngineSection data-id="element-1726" />
      <WhatWeAreNotSection data-id="element-1727" />
      <HowPeopleUseSection data-id="element-1728" />
      <PhilosophySection data-id="element-1729" />
      <FinalCTASection data-id="element-1730" />
      <Footer data-id="element-1731" />
    </main>;
}